import { useGetPendingPeLicenseRequest, useGetPrivateEditionLocalStatus } from "./private_edition_hooks";
import { PeLicenseInteractionType } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/pe_license_interaction_type_pb";
import { LicenseVaultCounterType } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/license_vault_counter_type_pb";
import { PrivateEditionLocalStatus } from "gc-web-proto/galaxycompletepb/apipb/pelocal_api_pb";
import { formatKnownDataType, KnownDataType } from "../../common/utils/formatter";
import { appI18n } from "../i18n/i18n";
import { getIsPrivateEdition } from "../auth/PrivateEditionView";

export const usePrivateEdition = (projectId?: string) => {
    const { data } = useGetPrivateEditionLocalStatus();
    const isPrivateEdition = getIsPrivateEdition();
    const isMultiTenant = isPrivateEdition && !!data?.status?.multiTenant;
    const isMainProject = isPrivateEdition && !!projectId && data?.status?.systemId === projectId;
    const canUsePersonalVault = !isPrivateEdition || isMultiTenant;

    return { isPrivateEdition, isMultiTenant, isMainProject, canUsePersonalVault, mainProjectName: data?.status?.mainProjectName };
};

export const useIsPELicenseActivated = () => {
    const { data } = useGetPrivateEditionLocalStatus();
    return !!data?.status?.licenseActivated;
};

export const useIsPELicenseNeedAuthentication = () => {
    const activated = useIsPELicenseActivated();
    const { data } = useGetPrivateEditionLocalStatus();
    return activated && !data?.status?.licenseAuthenticated;
};

export const useHasPendingPELicenseRequest = () => {
    const { data } = useGetPendingPeLicenseRequest();
    return !!data?.licenseRequest;
};

export const getPeLicenseInteractionTypeDisplayValue = (type: PeLicenseInteractionType.PeLicenseInteractionType, alreadyActivated?: boolean) => {
    if (type === PeLicenseInteractionType.PeLicenseInteractionType.ACTIVATION) {
        return alreadyActivated ? appI18n.t("privateEdition:interaction.renewal") : appI18n.t("privateEdition:interaction.activation");
    } else if (type === PeLicenseInteractionType.PeLicenseInteractionType.CHECK_IN) {
        return appI18n.t("privateEdition:interaction.checkIn");
    } else if (type === PeLicenseInteractionType.PeLicenseInteractionType.WITHDRAWAL) {
        return appI18n.t("privateEdition:interaction.withdrawal");
    } else {
        return appI18n.t("common:status.unknown");
    }
};

export const getPrivateEditionLicenseCounterDisplayValue = (counterType: LicenseVaultCounterType.LicenseVaultCounterType) => {
    if (counterType === LicenseVaultCounterType.LicenseVaultCounterType.GALAXY_MIGRATE_LOCAL_MIGRATION) {
        return appI18n.t("privateEdition:licenseCounter.types.blockStorageMigration");
    } else if (counterType === LicenseVaultCounterType.LicenseVaultCounterType.HOST_MIGRATION_LICENSE) {
        return appI18n.t("privateEdition:licenseCounter.types.hostMigration");
    } else if (counterType === LicenseVaultCounterType.LicenseVaultCounterType.HOST_UNLIMITED_MIGRATION) {
        return appI18n.t("privateEdition:licenseCounter.types.legacyUnlimitedMigration");
    } else if (counterType === LicenseVaultCounterType.LicenseVaultCounterType.HOST_MIGRATION_LICENSE30_DAYS_EXTENSION) {
        return appI18n.t("privateEdition:licenseCounter.types.hostMigrationExtension", { duration: "30d" });
    } else if (counterType === LicenseVaultCounterType.LicenseVaultCounterType.HOST_MIGRATION_LICENSE60_DAYS_EXTENSION) {
        return appI18n.t("privateEdition:licenseCounter.types.hostMigrationExtension", { duration: "60d" });
    } else if (counterType === LicenseVaultCounterType.LicenseVaultCounterType.HOST_MIGRATION_LICENSE1_TB_CAPACITY_EXTENSION) {
        return appI18n.t("privateEdition:licenseCounter.types.migrationCapacityAddOn", { capacity: "1TiB" });
    } else if (counterType === LicenseVaultCounterType.LicenseVaultCounterType.PRIVATE_EDITION_VIRTUAL_APPLIANCE) {
        return appI18n.t("privateEdition:licenseCounter.types.privateEditionVirtualAppliance", { duration: "60d" });
    } else if (counterType === LicenseVaultCounterType.LicenseVaultCounterType.UNSTRUCTURED_DATA_SYNCHRONIZATION) {
        return appI18n.t("privateEdition:licenseCounter.types.unstructuredDataMigration");
    } else if (counterType === LicenseVaultCounterType.LicenseVaultCounterType.DISASTER_RECOVERY_HOST_ENTITLEMENT) {
        return appI18n.t("privateEdition:licenseCounter.types.disasterRecoveryHost");
    }
    return appI18n.t("privateEdition:licenseCounter.types.unknown");
};

export const formatPrivateEditionLicenseCounterValue = (value: number, counterType: LicenseVaultCounterType.LicenseVaultCounterType) => {
    if (
        counterType === LicenseVaultCounterType.LicenseVaultCounterType.GALAXY_MIGRATE_LOCAL_MIGRATION ||
        counterType === LicenseVaultCounterType.LicenseVaultCounterType.UNSTRUCTURED_DATA_SYNCHRONIZATION
    ) {
        return formatKnownDataType(value, KnownDataType.CAPACITY);
    }
    if (
        counterType === LicenseVaultCounterType.LicenseVaultCounterType.HOST_MIGRATION_LICENSE ||
        counterType === LicenseVaultCounterType.LicenseVaultCounterType.HOST_UNLIMITED_MIGRATION ||
        counterType === LicenseVaultCounterType.LicenseVaultCounterType.DISASTER_RECOVERY_HOST_ENTITLEMENT
    ) {
        return appI18n.t("privateEdition:licenseCounter.values.host", { count: value });
    }
    if (
        counterType === LicenseVaultCounterType.LicenseVaultCounterType.HOST_MIGRATION_LICENSE30_DAYS_EXTENSION ||
        counterType === LicenseVaultCounterType.LicenseVaultCounterType.HOST_MIGRATION_LICENSE60_DAYS_EXTENSION ||
        counterType === LicenseVaultCounterType.LicenseVaultCounterType.HOST_MIGRATION_LICENSE1_TB_CAPACITY_EXTENSION ||
        counterType === LicenseVaultCounterType.LicenseVaultCounterType.PRIVATE_EDITION_VIRTUAL_APPLIANCE
    ) {
        return appI18n.t("privateEdition:licenseCounter.values.license", { count: value });
    }
    return formatKnownDataType(value, KnownDataType.NUMBER);
};

export const getCdcSaaSUrlForThisPE = (peLocalStatus: PrivateEditionLocalStatus.AsObject) => {
    return !!peLocalStatus?.cdcProjectId ? `https://cloud.cirrusdata.com/projects/${peLocalStatus?.cdcProjectId}` : "https://cloud.cirrusdata.com/";
};
